import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Sun, Moon } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { Logo } from "@/components/brand/Logo";
import { AuthHero, AuthShapes } from "./AuthHero";
import "./auth-canvas.css";

function AuthToggles() {
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const cycleLang = () => {
        const order = ["az", "en", "ru"];
        const idx = order.indexOf(i18n.language.split("-")[0]);
        i18n.changeLanguage(order[(idx + 1) % order.length]);
    };
    const isLight = theme === "light" || (theme === "system" && document.documentElement.classList.contains("light"));
    const cycleTheme = () => setTheme(isLight ? "dark" : "light");
    const ThemeIcon = isLight ? Sun : Moon;
    const themeLabel = isLight ? t("topbar.theme.light") : t("topbar.theme.dark");
    const btn = "inline-flex items-center justify-center rounded-lg border transition-colors hover:bg-[var(--input)]";
    const st = { minWidth: 36, minHeight: 36, color: "var(--text-muted)", borderColor: "var(--border)" };
    return (
        <div className="ml-auto flex items-center gap-1.5">
            <button data-testid="auth-theme-switch" onClick={cycleTheme} title={themeLabel} aria-label={themeLabel} className={btn} style={st}>
                <ThemeIcon size={15} />
            </button>
            <button data-testid="auth-lang-switch" onClick={cycleLang} className={`${btn} text-[11px] uppercase tracking-wider px-2`} style={st}>
                {i18n.language.startsWith("ru") ? "RU" : i18n.language.startsWith("en") ? "EN" : "AZ"}
            </button>
        </div>
    );
}

export function AuthLayout({ title, children, backHref = "/", backLabelKey = "auth.back_home" }: {
    title: string;
    children: React.ReactNode;
    backHref?: string;
    backLabelKey?: string;
}) {
    const { t } = useTranslation();
    const back = t(backLabelKey as any, { defaultValue: t("common.back") });
    return (
        <div className="auth-canvas" data-testid="auth-canvas">
            <AuthShapes />
            <div className="relative grid min-h-screen lg:grid-cols-[38%_1fr]">
                <section className="auth-pane flex flex-col px-6 py-8 sm:px-12 lg:min-h-screen lg:px-[4.5vw] lg:py-10" data-testid="auth-card">
                    <div className="flex items-center gap-3">
                        <Link to="/" aria-label="Service LAB" data-testid="auth-brand" className="inline-flex items-center gap-2">
                            <Logo variant="icon" height={30} testId="auth-brand-logo" />
                            <span className="auth-font text-lg font-medium tracking-tight" style={{ color: "var(--text)" }}>Service LAB</span>
                        </Link>
                        <AuthToggles />
                    </div>
                    <div className="flex flex-1 items-center justify-center py-10">
                        <div className="w-full max-w-[360px] animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
                            <Link to={backHref} data-testid="auth-back-link" aria-label={back}
                                className="mb-4 inline-flex items-center gap-1 text-xs hover:underline" style={{ color: "var(--text-muted)" }}>
                                <ArrowLeft size={13} /> {back}
                            </Link>
                            <div className="auth-form-card rounded-2xl p-5 sm:p-6">
                                <h1 className="auth-font text-lg font-semibold mb-4" style={{ color: "var(--text)" }} data-testid="auth-title">{title}</h1>
                                {children}
                            </div>
                        </div>
                    </div>
                </section>
                <section className="relative flex items-center px-6 py-16 sm:px-12 lg:pl-[9%] lg:pr-10">
                    <AuthHero />
                </section>
            </div>
        </div>
    );
}

export function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <label className="block">
            <div className="text-xs mb-1.5" style={{ color: "var(--text)" }}>{label}</div>
            {children}
        </label>
    );
}
